import React, { useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { RowData } from "@tanstack/react-table-v9";

import TableV2ExportMenu from "./components/TableV2ExportMenu";
import TableV2Grid from "./components/TableV2Grid";
import type { TableV2GridProps } from "./components/TableV2Grid";
import TableV2IconButton from "./components/TableV2IconButton";
import TableV2Pagination from "./components/TableV2Pagination";
import TableV2Toolbar from "./components/TableV2Toolbar";
import type { TableV2ToolbarProps } from "./components/TableV2Toolbar";
import TableV2ViewSwitch from "./components/TableV2ViewSwitch";
import type { TableV2ExportOptions } from "./export/exportTable";
import { useFillHeight } from "./hooks/useFillHeight";
import PivotBuilder from "./pivot/PivotBuilder";
import PivotDetailPanel from "./pivot/PivotDetailPanel";
import { TableContainer, TableFrame } from "./styles";

export type TableV2Props<TData extends RowData> = TableV2GridProps<TData> &
  Omit<TableV2ToolbarProps<TData>, "table" | "isLoading"> & {
    /** Pair with `useTableV2({ disablePagination: true })` */
    hidePagination?: boolean;
    pageSizeOptions?: number[];
    style?: CSSProperties;
    /** Pivot view: shows the Rows / Columns / Values setup button in the header corner */
    pivotConfigurable?: boolean;
    pivotSetupOpen?: boolean;
    /** Caps the body to the space left in the viewport so the page doesn't scroll */
    fillHeight?: boolean;
    exportOptions?: TableV2ExportOptions<TData>;
  };

const TableV2 = <TData extends RowData>({
  table,
  isLoading,
  hidePagination = false,
  pageSizeOptions,
  style,
  headerText,
  primaryButton,
  actions,
  search,
  advancedFilter,
  selectionMenuItems,
  exportAction,
  exportOptions,
  inlineActions,
  pivotConfigurable = true,
  pivotSetupOpen = false,
  fillHeight = true,
  ...gridProps
}: TableV2Props<TData>) => {
  const [setupOpen, setSetupOpen] = useState(pivotSetupOpen);
  const pivot = table.v2?.view === "pivot" ? table.v2.pivot : null;
  const gridRef = useRef<HTMLDivElement>(null);
  const belowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const filledHeight = useFillHeight({
    enabled: fillHeight && gridProps.maxBodyHeight === undefined,
    gridRef,
    belowRef,
    containerRef,
  });
  const maxBodyHeight = gridProps.maxBodyHeight ?? filledHeight;

  const exportButton = isLoading ? null : exportOptions ? (
    <TableV2ExportMenu key="export" table={table} options={exportOptions} />
  ) : (
    exportAction && (
      <TableV2IconButton
        key="export"
        icon="fa-solid fa-download"
        label="Export"
        onClick={exportAction}
      />
    )
  );

  const headerActions = [
    table.v2?.pivot && <TableV2ViewSwitch key="view" table={table} />,
    pivot && pivotConfigurable && (
      <TableV2IconButton
        key="setup"
        icon="fa-solid fa-sliders"
        label="Set up pivot"
        active={setupOpen}
        onClick={() => setSetupOpen((open) => !open)}
      />
    ),
    exportButton,
  ];

  const shared = {
    emptyMessage: gridProps.emptyMessage,
    hideHeader: gridProps.hideHeader,
    maxBodyHeight,
    columnMenu: gridProps.columnMenu,
    columnDrag: gridProps.columnDrag,
    columnPicker: gridProps.columnPicker,
    headerActions,
  };

  return (
    <TableContainer ref={containerRef} style={style}>
      <TableV2Toolbar
        table={table}
        isLoading={isLoading}
        headerText={headerText}
        primaryButton={primaryButton}
        actions={actions}
        search={search}
        advancedFilter={advancedFilter}
        selectionMenuItems={pivot ? undefined : selectionMenuItems}
        inlineActions={inlineActions}
      />
      {pivot ? (
        <>
          {pivotConfigurable && setupOpen && (
            <PivotBuilder
              pivot={pivot.controller}
              onClose={() => setSetupOpen(false)}
            />
          )}
          <TableFrame ref={gridRef}>
            <TableV2Grid
              {...shared}
              table={pivot.table}
              isLoading={isLoading || pivot.controller.isLoading}
              error={pivot.controller.error ?? gridProps.error}
              onRetry={
                pivot.controller.error
                  ? pivot.controller.retry
                  : gridProps.onRetry
              }
              rowEmphasis={(row) => row.original.hasChildren}
              stableHeight={false}
            />
          </TableFrame>
          {pivot.drillToDetail && (
            <PivotDetailPanel
              detail={pivot.detail}
              columns={table.options.columns}
              fields={pivot.controller.fields}
              renderTable={(detail) => (
                <TableV2
                  search
                  table={detail.table}
                  isLoading={detail.isLoading}
                  error={detail.error}
                  onRetry={detail.onRetry}
                  exportOptions={{ fileName: detail.fileName }}
                />
              )}
            />
          )}
        </>
      ) : (
        <TableFrame>
          <div ref={gridRef}>
            <TableV2Grid
              table={table}
              isLoading={isLoading}
              {...gridProps}
              maxBodyHeight={maxBodyHeight}
              headerActions={headerActions}
            />
          </div>
          {!hidePagination && (
            <div ref={belowRef}>
              <TableV2Pagination
                table={table}
                isLoading={isLoading}
                pageSizeOptions={pageSizeOptions}
              />
            </div>
          )}
        </TableFrame>
      )}
    </TableContainer>
  );
};

export default TableV2;
